import React, { useState, useEffect, useMemo } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  LineChart, Line,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getApiKey = () => localStorage.getItem('quod_api_key') || '';

const VECTOR_COLORS = [
  '#DC2626', '#2563EB', '#059669', '#D97706', '#7C3AED',
  '#DB2777', '#0891B2', '#65A30D', '#EA580C', '#4F46E5',
];

const DIM_LABELS = {
  operational_distress: 'Op. Distress',
  delivery_friction: 'Delivery Friction',
  governance_leadership: 'Governance',
  customer_deterioration: 'Customer Exp.',
  vendor_platform_stress: 'Vendor Stress',
  transformation_trigger: 'Transformation',
  intervention_potential: 'Intervention',
  evidence_quality: 'Evidence Quality',
};

const COMPOSITE_KEYS = [
  { key: 'overall_risk_score', label: 'Risk' },
  { key: 'opportunity_score', label: 'Opportunity' },
  { key: 'change_signal_strength', label: 'Change' },
  { key: 'intervention_likelihood', label: 'Intervention' },
  { key: 'evidence_quality_score', label: 'Evidence' },
];

// ── Quick Compare (inline in pipeline trend) ──
export function QuickCompare({ companyId, companyName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/analysis/compare/${companyId}`, {
          headers: { 'X-API-Key': getApiKey() },
        });
        if (resp.ok) setData(await resp.json());
      } catch {}
      setLoading(false);
    };
    load();
  }, [companyId]);

  if (loading) return <div style={{ padding: '8px', fontSize: '11px', color: 'var(--text-secondary)' }}>Loading comparison...</div>;
  if (!data || data.vector_count < 2) return <div style={{ padding: '8px', fontSize: '11px', color: 'var(--text-secondary)' }}>Need 2+ scoped analyses for comparison. Run analyses with different vectors first.</div>;

  const radarData = Object.entries(DIM_LABELS).map(([key, label]) => {
    const entry = { dimension: label };
    data.vectors.forEach((v, i) => {
      entry[v.scope_label] = v.dimensions[key] || 0;
    });
    return entry;
  });

  return (
    <div data-testid="quick-compare">
      <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px' }}>
        Vector Comparison — {data.vector_count} scopes
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
        {data.vectors.map((v, i) => (
          <span key={i} style={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: '3px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: VECTOR_COLORS[i % VECTOR_COLORS.length], display: 'inline-block' }} />
            {v.scope_label} (Risk: {v.composite.overall_risk_score})
          </span>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <RadarChart data={radarData}>
          <PolarGrid stroke="var(--border)" />
          <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 9, fill: 'var(--text-secondary)' }} />
          <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 8 }} />
          {data.vectors.map((v, i) => (
            <Radar key={v.scope_label} name={v.scope_label} dataKey={v.scope_label}
              stroke={VECTOR_COLORS[i % VECTOR_COLORS.length]}
              fill={VECTOR_COLORS[i % VECTOR_COLORS.length]}
              fillOpacity={0.08} strokeWidth={1.5} />
          ))}
          <Tooltip contentStyle={{ fontSize: '10px', background: 'var(--surface)', border: '1px solid var(--border)' }} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Full Comparison View ──
export default function VectorComparison({ companyId, companyName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('radar');
  const [selectedVectors, setSelectedVectors] = useState(new Set());

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/analysis/compare/${companyId}`, {
          headers: { 'X-API-Key': getApiKey() },
        });
        if (resp.ok) {
          const d = await resp.json();
          setData(d);
          setSelectedVectors(new Set(d.vectors.map((_, i) => i)));
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [companyId]);

  const activeVectors = useMemo(() => {
    if (!data) return [];
    return data.vectors.filter((_, i) => selectedVectors.has(i));
  }, [data, selectedVectors]);

  const toggleVector = (idx) => {
    setSelectedVectors(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading comparison data...</div>;
  if (!data || data.vector_count < 2) return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <div style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>Need 2+ scoped analyses for comparison. Run analyses with different vector/sub_vector scopes first.</div>
      {onClose && <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>}
    </div>
  );

  // ── Chart Data ──
  const radarData = Object.entries(DIM_LABELS).map(([key, label]) => {
    const entry = { dimension: label };
    activeVectors.forEach(v => { entry[v.scope_label] = v.dimensions[key] || 0; });
    return entry;
  });

  const barData = Object.entries(DIM_LABELS).map(([key, label]) => {
    const entry = { dimension: label };
    activeVectors.forEach(v => { entry[v.scope_label] = v.dimensions[key] || 0; });
    return entry;
  });

  const compositeBarData = COMPOSITE_KEYS.map(({ key, label }) => {
    const entry = { metric: label };
    activeVectors.forEach(v => { entry[v.scope_label] = v.composite[key] || 0; });
    return entry;
  });

  // Merge trend histories (align by index since timestamps differ per vector)
  const maxHistLen = Math.max(...activeVectors.map(v => (v.history || []).length), 0);
  const trendData = [];
  for (let i = 0; i < maxHistLen; i++) {
    const point = { index: i + 1 };
    activeVectors.forEach(v => {
      const h = (v.history || [])[i];
      if (h) {
        point[`${v.scope_label}_risk`] = h.overall_risk_score;
        point[`${v.scope_label}_opp`] = h.opportunity_score;
      }
    });
    trendData.push(point);
  }

  const tabs = [
    { id: 'radar', label: 'Radar' },
    { id: 'dimensions', label: 'Dimensions' },
    { id: 'composite', label: 'Composite Scores' },
    { id: 'trends', label: 'Trend Overlay' },
  ];

  return (
    <div data-testid="vector-comparison" style={{ padding: '0' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '700' }}>Vector Comparison: {companyName}</div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{data.vector_count} scopes analyzed</div>
        </div>
        {onClose && <button className="btn btn-outline btn-sm" onClick={onClose} data-testid="comparison-close">Close</button>}
      </div>

      {/* Vector toggles */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
        {data.vectors.map((v, i) => (
          <label key={i} style={{
            display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
            padding: '3px 8px', borderRadius: '4px', fontSize: '11px',
            background: selectedVectors.has(i) ? `${VECTOR_COLORS[i % VECTOR_COLORS.length]}15` : 'transparent',
            border: `1px solid ${selectedVectors.has(i) ? VECTOR_COLORS[i % VECTOR_COLORS.length] : 'var(--border)'}`,
          }}>
            <input type="checkbox" checked={selectedVectors.has(i)} onChange={() => toggleVector(i)} style={{ accentColor: VECTOR_COLORS[i % VECTOR_COLORS.length] }} />
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: VECTOR_COLORS[i % VECTOR_COLORS.length] }} />
            <span style={{ fontWeight: '600' }}>{v.scope_label}</span>
            <span style={{ color: 'var(--text-secondary)' }}>Risk: {v.composite.overall_risk_score}</span>
          </label>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '2px', marginBottom: '8px', borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`btn btn-sm ${activeTab === t.id ? 'btn-primary' : 'btn-outline'}`}
            style={{ borderRadius: '4px 4px 0 0', fontSize: '11px', padding: '4px 12px' }}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'radar' && (
        <ResponsiveContainer width="100%" height={320}>
          <RadarChart data={radarData}>
            <PolarGrid stroke="var(--border)" />
            <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} />
            <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
            {activeVectors.map((v, i) => {
              const ci = data.vectors.indexOf(v);
              return (
                <Radar key={v.scope_label} name={v.scope_label} dataKey={v.scope_label}
                  stroke={VECTOR_COLORS[ci % VECTOR_COLORS.length]}
                  fill={VECTOR_COLORS[ci % VECTOR_COLORS.length]}
                  fillOpacity={0.1} strokeWidth={2} />
              );
            })}
            <Legend wrapperStyle={{ fontSize: '10px' }} />
            <Tooltip contentStyle={{ fontSize: '10px', background: 'var(--surface)', border: '1px solid var(--border)' }} />
          </RadarChart>
        </ResponsiveContainer>
      )}

      {activeTab === 'dimensions' && (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={barData} layout="vertical" margin={{ left: 90 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
            <YAxis dataKey="dimension" type="category" tick={{ fontSize: 10 }} width={85} />
            {activeVectors.map((v, i) => {
              const ci = data.vectors.indexOf(v);
              return <Bar key={v.scope_label} dataKey={v.scope_label} fill={VECTOR_COLORS[ci % VECTOR_COLORS.length]} />;
            })}
            <Tooltip contentStyle={{ fontSize: '10px', background: 'var(--surface)', border: '1px solid var(--border)' }} />
            <Legend wrapperStyle={{ fontSize: '10px' }} />
          </BarChart>
        </ResponsiveContainer>
      )}

      {activeTab === 'composite' && (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={compositeBarData} margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="metric" tick={{ fontSize: 10 }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
            {activeVectors.map((v, i) => {
              const ci = data.vectors.indexOf(v);
              return <Bar key={v.scope_label} dataKey={v.scope_label} fill={VECTOR_COLORS[ci % VECTOR_COLORS.length]} />;
            })}
            <Tooltip contentStyle={{ fontSize: '10px', background: 'var(--surface)', border: '1px solid var(--border)' }} />
            <Legend wrapperStyle={{ fontSize: '10px' }} />
          </BarChart>
        </ResponsiveContainer>
      )}

      {activeTab === 'trends' && (
        <div>
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
            Risk score over time (solid) &middot; Opportunity score (dashed)
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trendData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="index" tick={{ fontSize: 10 }} label={{ value: 'Analysis #', position: 'insideBottom', fontSize: 10, offset: -2 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              {activeVectors.map((v, i) => {
                const ci = data.vectors.indexOf(v);
                const color = VECTOR_COLORS[ci % VECTOR_COLORS.length];
                return (
                  <React.Fragment key={v.scope_label}>
                    <Line type="monotone" dataKey={`${v.scope_label}_risk`} stroke={color}
                      strokeWidth={2} dot={{ r: 2 }} name={`${v.scope_label} Risk`} connectNulls />
                    <Line type="monotone" dataKey={`${v.scope_label}_opp`} stroke={color}
                      strokeWidth={1.5} strokeDasharray="4 4" dot={false} name={`${v.scope_label} Opp`} connectNulls />
                  </React.Fragment>
                );
              })}
              <Tooltip contentStyle={{ fontSize: '10px', background: 'var(--surface)', border: '1px solid var(--border)' }} />
              <Legend wrapperStyle={{ fontSize: '9px' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Summary table */}
      <div style={{ marginTop: '8px', overflow: 'auto' }}>
        <table className="data-table" style={{ fontSize: '11px' }} data-testid="comparison-table">
          <thead>
            <tr>
              <th>Scope</th>
              <th>Risk</th>
              <th>Opp.</th>
              <th>Change</th>
              <th>Interv.</th>
              <th>Evidence</th>
              <th>Signals</th>
              <th>Recommendation</th>
            </tr>
          </thead>
          <tbody>
            {activeVectors.map((v, i) => {
              const ci = data.vectors.indexOf(v);
              return (
                <tr key={ci}>
                  <td style={{ fontWeight: '600' }}>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: VECTOR_COLORS[ci % VECTOR_COLORS.length], marginRight: '4px' }} />
                    {v.scope_label}
                  </td>
                  <td style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{v.composite.overall_risk_score}</td>
                  <td style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{v.composite.opportunity_score}</td>
                  <td style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{v.composite.change_signal_strength}</td>
                  <td style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{v.composite.intervention_likelihood}</td>
                  <td style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{v.composite.evidence_quality_score}</td>
                  <td style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{v.signal_count}</td>
                  <td>
                    <span className={`badge ${v.recommendation?.includes('high') ? 'badge-high' : v.recommendation?.includes('medium') ? 'badge-medium' : 'badge-low'}`}>
                      {(v.recommendation || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
